<!--
  This component is used in the KPI or PSI Overview page of a project.
  It consists of a file-upload form and a button that downloads a sample file.
  The user can download the sample file, which contains the KPIs/PSIs with their
  current values, to edit it with the new values.
  Then, they can upload the edited file from the file-upload form. If the process
  is successful the values in the table should be updated.
-->
<template>

  <v-dialog max-width="500">
    <template v-slot:activator="{ props: activatorProps }">
      <v-btn
          v-bind="activatorProps"
          text="Update values"
          color="green-darken-4"
          variant="flat"
      ></v-btn>
    </template>

    <template v-slot:default="{ isActive }">
      <v-card>
        <v-card-title><b>Upload updated values file</b></v-card-title>
        <v-divider />
        <v-card-text>
          Select and upload a file with the expected format to bulk update the project's values.
          To download a template file click the button "DOWNLOAD TEMPLATE" below the KPIs table.

          <v-spacer></v-spacer>

          <v-file-input
              label="Select a file..."
              v-model="selectedFile"
              accept=".csv"
              rounded
              variant="outlined"
              required
              class="mt-3"
          ></v-file-input>
          <v-alert
              v-if="errors.length > 0"
              type="error"
              variant="tonal"
              class="my-2"
              dismissible
              density="compact"
              style="width: fit-content;"
          >
            {{ errors }}
          </v-alert>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer />

          <v-btn
              text="Close"
              color="red"
              class="px-4"
              rounded
              @click="isActive.value = false"
          />

          <v-btn
              text="Submit"
              color="blue-darken-4"
              class="px-4"
              variant="flat"
              rounded
              prepend-icon="fas fa-check mr-1"
              @click="submitFile(isActive)"
          ></v-btn>


        </v-card-actions>
      </v-card>
    </template>
  </v-dialog>

  <v-btn
      :href="templateFilePath"
      :download="templateFileName"
      text="DOWNLOAD TEMPLATE"
      color="blue-darken-4"
      prepend-icon="fas fa-download"
      class="text-none ml-2"
  />


</template>

<script setup>

import {computed, ref} from "vue";
import * as $routes from "@/packs/routes";
import {router} from "@inertiajs/vue3";

const props = defineProps({
  /**
   * The id of the project of which the values will be updated.
   */
  projectId: {
    type: Number,
    required: true
  },
  /**
   * Defines whether the dialog will be used for uploading
   * KPIs or PSIs. This will be used to decide the route
   * to submit the file. Acceptable values: { 'kpi', 'psi' }.
   */
  method: {
    type: String,
    required: true
  },
  /**
   * Errors that were produced from the back end during file upload.
   */
  errors: {
    type: Array,
    default: [],
    required: false
  }
})

const selectedFile = ref(null)
/**
 * The name of the file in the project's files. It will also be used
 * as the name of the file that will be downloaded to the user's browser.
 */
const templateFileName = computed(() => {
  return `${props.method}_values_upload_template.csv`
})
/**
 * Points to the correct path to download the template file.
 * The template file resides in `app/public/import-templates` and it
 * is different for the KPIs and the PSIs.
 */
const templateFilePath = computed(() => {
  return `/import-templates/${templateFileName.value}`
})

/**
 * Submits the file, closes the dialog and resets the provided inputs.
 * @param {ref} isActive - A ref object with boolean value. It is necessary because the dialog uses its own
 * scopes variable that decides its visibility.
 */
function submitFile(isActive) {
  const routes = { kpi: $routes.update_kpis_values_project_kpis, psi: $routes.update_psis_values_project_psis }
  const route = routes[props.method]
  // https://inertiajs.com/manual-visits#state-preservation
  router.post(route(props.projectId), { uploaded_csv_file: selectedFile.value }, { preserveState: 'errors' })
}

</script>

<style scoped>

</style>